import { getActiveContextType } from '@/auth/activeAssignment';
import { hasAnyKey } from '@/auth/writeKeys';
import { ApiProvider } from '@/provider/ApiProvider';

/**
 * Page gate (authorization part 7g, owner decision 2026-10-07).
 *
 * A page opens only when the active role holds the key of at least ONE of its tabs
 * (ANY(page)); the server answers the page's header / shell endpoints for ANY(page) and keeps
 * the tab key on tab-only endpoints. The sets mirror pnet-docs/authz-review/
 * part7g-page-gate-spec.md ("Page key sets"); a change on either side must change both.
 */
export const PAGE_KEYS = {
  /** ANY(CV): contract view `/viewcontract/:id`, one key per tab. */
  contractView: [
    'contract_general_view',
    'strategic_fit_view',
    'bonus_view',
    'contract_investment_view',
    'strategic_market_view',
    'return_of_objective_view',
    'rights_view',
    'return_of_investments_view',
    'documents_view',
  ],
  /**
   * ANY(BP): brand profile `/brandprofile/:id`. The Profile tab also accepts
   * brand_profile_summary_view (summary variant), and so does the server's ANY(BP).
   */
  brandProfile: [
    'brand_profile_view_profile_tab',
    'brand_profile_summary_view',
    'brand_insights_view',
    'advocacy_view',
    'brand_activations_view',
    'brand_audience_view',
    'brand_contract_view',
    'brand_others_view',
    'commercial_playbook_view',
    'brand_scouting_view',
    'brand_profile_view_product_tab',
    'strategic_cluster_permission',
    'commercial_playbook_v2_view',
    // New Brand Profile summary tabs (brandProfileTabs.js). A role granted only one
    // of these must still clear the page gate, so ANY(BP) includes them (Ademola,
    // PR #2176); brand_profile_summary_view above already covers the Summary tab.
    'brand_profile_markets_view',
    'brand_profile_media_costs_view',
    'brand_profile_brand_health_view',
    'brand_profile_products_view',
    'brand_profile_social_view',
    'brand_profile_news_view',
    'brand_profile_stores_view',
    'brand_profile_baseline_view',
    'brand_profile_followership_view',
    'brand_profile_documents_view',
    'brand_profile_branding_view',
  ],
  /** ANY(PP): partner profile `/partnerProfile/...` (and `view_partner_profile`, see below). */
  partnerProfile: [
    'partner_profile_view_profile_tab',
    'property_tracker_view',
    'property_activations_view',
    'property_advocacy_view',
    'assets_view',
    'property_audience_view',
    'insights_view',
    'partner_portfolio_view',
    'inventory_view',
    'marketability_view',
    'partner_profile_view_media_tab',
    'partner_profile_view_performance_tab',
    'partner_profile_view_product_tab',
    'property_roo_view',
    'sponsors_view',
  ],
  /** Comparison entry: `/comparison`, `/comparison/activation/:id`, `/comparison/competition`. */
  comparison: ['tools_compare', 'compare_view'],
  /** ANY(CMPV): the sections of `/comparison/view` (entered with a comparison key). */
  comparisonView: [
    'head_to_head_summary_graph',
    'head_to_head_additional_graph',
    'head_to_head_channel_deep_dive_graph',
    'head_to_head_period_analysis_graph',
  ],
  /** ANY(H2H): the sections of `/headtohead` (entered with a comparison key). */
  headToHead: [
    'performance',
    'reach',
    'roo_property_score',
    'marketability',
    'marketability_share_of_search',
    'web_traffic',
    'market_value',
    'reach_overlap',
  ],
} as const;

export type PageKeySet = keyof typeof PAGE_KEYS;

/** The partner profile page also needs this key (AND ANY(PP)). */
export const PARTNER_PROFILE_KEY = 'view_partner_profile';

/** Activity view `/viewActivity/:id` (Activation/GetActivationProfile needs it, part 7c). */
export const ACTIVITY_VIEW_KEY = 'activation_view';

/** Campaign (activation) view `/viewActivation/:id`. */
export const CAMPAIGN_VIEW_KEY = 'campaign_view';

/**
 * Activity / activation list `/listview/activities` and `/listview/activation` (part 7j): the
 * page shows the list with either key (pages/listview), so a link to it needs one of them.
 */
export const ACTIVATION_LIST_KEYS = ['activation_list_view', 'campaign_list_view'] as const;

type HasKey = typeof ApiProvider.permission;

const roleHasKey: HasKey = (key) => Boolean(ApiProvider.permission(key));

/** Pages behind the gate and what each needs. */
export type GatedPage =
  | 'contractView'
  | 'brandProfile'
  | 'partnerProfile'
  | 'comparison'
  | 'comparisonView'
  | 'headToHead'
  | 'campaignView'
  | 'activityView'
  | 'activationList';

/** True when the active role may open the page. */
export function canOpenPage(page: GatedPage, has: HasKey = roleHasKey): boolean {
  switch (page) {
    case 'contractView':
      return hasAnyKey(PAGE_KEYS.contractView, has);
    case 'brandProfile':
      return hasAnyKey(PAGE_KEYS.brandProfile, has);
    case 'partnerProfile':
      return Boolean(has(PARTNER_PROFILE_KEY)) && hasAnyKey(PAGE_KEYS.partnerProfile, has);
    case 'comparison':
      return hasAnyKey(PAGE_KEYS.comparison, has);
    case 'comparisonView':
      return hasAnyKey(PAGE_KEYS.comparison, has) && hasAnyKey(PAGE_KEYS.comparisonView, has);
    case 'headToHead':
      return hasAnyKey(PAGE_KEYS.comparison, has) && hasAnyKey(PAGE_KEYS.headToHead, has);
    case 'campaignView':
      return Boolean(has(CAMPAIGN_VIEW_KEY));
    case 'activityView':
      return Boolean(has(ACTIVITY_VIEW_KEY));
    case 'activationList':
      return hasAnyKey(ACTIVATION_LIST_KEYS, has);
    default:
      return false;
  }
}

/**
 * Contract data keys (open question T4, owner 2026-10-08): the contract list / brand contract
 * tab keys and every contract view tab key. Only a role holding one of them can ever use
 * contract data, so only such a role is offered the Contract Data and Responsibility Agreement.
 */
export const CONTRACT_DATA_KEYS = [
  'contract_list_view',
  'contract_list',
  'brand_contract_view',
  ...PAGE_KEYS.contractView,
] as const;

/** True when the active role holds any contract data key (may accept the agreement). */
export function hasContractDataKey(has: HasKey = roleHasKey): boolean {
  return hasAnyKey(CONTRACT_DATA_KEYS, has);
}

/**
 * Global search sections (part 8a, item 7): a section (result tab and its rows in "Best
 * Results") is shown only when the active role holds at least ONE key of that data type: its
 * list key, or any key of the page a row opens. Without the page key the rows of a shown
 * section stay plain text (owner decisions C and T5); a role with no key of the type at all
 * does not see the section. The results and totals themselves are the server's (scope).
 */
export const SEARCH_SECTION_KEYS = {
  properties: ['property_list_view', PARTNER_PROFILE_KEY, ...PAGE_KEYS.partnerProfile],
  brands: ['brand_list_view', ...PAGE_KEYS.brandProfile],
  contracts: CONTRACT_DATA_KEYS,
  activations: ['activation_list_view', 'activation_list', ACTIVITY_VIEW_KEY],
  campaigns: ['campaign_list_view', CAMPAIGN_VIEW_KEY],
} as const;

export type SearchSection = keyof typeof SEARCH_SECTION_KEYS;

/** True when the active role may see that section of the global search. */
export function canSeeSearchSection(section: SearchSection, has: HasKey = roleHasKey): boolean {
  return hasAnyKey(SEARCH_SECTION_KEYS[section], has);
}

/** The key the property service requires for partner master data edits (logo, General tab). */
export const PARTNER_MASTER_DATA_EDIT_KEY = 'property_tracker_edit';

/**
 * Partner master data (logo, General tab fields) is edited by internal users only (item 6,
 * plan §13.21): the property service answers Property/update and UpdateProfileLogo only for an
 * INTERNAL active context holding property_tracker_edit, so the edit controls follow the same rule.
 */
export function canEditPartnerMasterData(
  has: HasKey = roleHasKey,
  contextType: string | null = getActiveContextType(),
): boolean {
  return contextType === 'INTERNAL' && Boolean(has(PARTNER_MASTER_DATA_EDIT_KEY));
}
